import { useState, useEffect, useRef, useCallback } from 'react';
import { API_CONFIG } from '../config/api';
import { auth } from '../config/firebase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EntityLogEntry {
  name: string;
  category: string;
  merged: boolean;
  storedAt: string;
}

export interface PageStatusEntry {
  url: string;
  status: 'ok' | 'failed';
  error?: string;
  retried?: boolean;
}

export interface ScrapingProgressState {
  connected: boolean;
  active: boolean;
  status: string | null;
  phase: string | null;
  percentage: number;
  processedUrls: number;
  totalUrls: number;
  entitiesExtracted: number;
  currentUrl: string | null;
  /** Estimated completion time (epoch ms), from the server's rolling crawl rate. */
  etaMs: number | null;
  discoveredUrls: string[];
  scrapedPageTitles: Array<{ url: string; title: string }>;
  entityLog: EntityLogEntry[];
  pageStatuses: PageStatusEntry[];
  pdfsExtracted: number;
  errors: string[];
  completed: boolean;
  failed: boolean;
}

interface WsProgressData {
  status?: string;
  progress?: {
    phase?: string;
    percentage?: number;
    processedUrls?: number;
    totalUrls?: number;
    entitiesExtracted?: number;
    currentUrl?: string;
    estimatedCompletion?: unknown;
    entityLog?: EntityLogEntry[];
  };
  results?: {
    discoveredUrls?: string[];
    scrapedPages?: number;
    scrapedPageTitles?: Array<{ url: string; title: string }>;
    extractedEntities?: number;
    pageStatuses?: PageStatusEntry[];
    pdfsExtracted?: number;
    errors?: string[];
  };
}

/** Server timestamps arrive as ISO strings or Firestore {_seconds} objects. */
function timestampToMillis(value: unknown): number | null {
  if (!value) return null;
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isNaN(t) ? null : t;
  }
  if (typeof value === 'object') {
    const o = value as { _seconds?: number; seconds?: number };
    const s = o._seconds ?? o.seconds;
    if (typeof s === 'number') return s * 1000;
  }
  return null;
}

interface WsMessage {
  type: string;
  jobId?: string;
  message?: string;
  data?: WsProgressData;
  timestamp?: string;
  status?: string;
}

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const INITIAL_STATE: ScrapingProgressState = {
  connected: false,
  active: false,
  status: null,
  phase: null,
  percentage: 0,
  processedUrls: 0,
  totalUrls: 0,
  entitiesExtracted: 0,
  currentUrl: null,
  etaMs: null,
  discoveredUrls: [],
  scrapedPageTitles: [],
  entityLog: [],
  pageStatuses: [],
  pdfsExtracted: 0,
  errors: [],
  completed: false,
  failed: false,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useScrapingProgress(jobId: string | null): ScrapingProgressState {
  const [state, setState] = useState<ScrapingProgressState>(INITIAL_STATE);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasReconnectedRef = useRef(false);
  const mountedRef = useRef(true);

  const applyData = useCallback((data: WsProgressData, terminal: 'completed' | 'failed' | null) => {
    setState((prev) => {
      // Merge entityLog: keep existing entries, add new ones from server
      const serverLog = data.progress?.entityLog ?? [];
      let mergedLog = prev.entityLog;
      if (serverLog.length > 0) {
        // The server sends the full log — use whichever is longer/newer
        mergedLog = serverLog.length >= prev.entityLog.length ? serverLog : prev.entityLog;
      }

      return {
        ...prev,
        active: terminal === null,
        status: data.status ?? prev.status,
        phase: data.progress?.phase ?? prev.phase,
        percentage: data.progress?.percentage ?? prev.percentage,
        processedUrls: data.progress?.processedUrls ?? prev.processedUrls,
        totalUrls: data.progress?.totalUrls ?? prev.totalUrls,
        entitiesExtracted: data.progress?.entitiesExtracted ?? prev.entitiesExtracted,
        currentUrl: data.progress?.currentUrl ?? prev.currentUrl,
        etaMs: timestampToMillis(data.progress?.estimatedCompletion) ?? prev.etaMs,
        discoveredUrls: data.results?.discoveredUrls ?? prev.discoveredUrls,
        scrapedPageTitles: data.results?.scrapedPageTitles ?? prev.scrapedPageTitles,
        entityLog: mergedLog.slice(0, 100),
        pageStatuses: data.results?.pageStatuses ?? prev.pageStatuses,
        pdfsExtracted: data.results?.pdfsExtracted ?? prev.pdfsExtracted,
        errors: data.results?.errors ?? prev.errors,
        completed: terminal === 'completed' ? true : prev.completed,
        failed: terminal === 'failed' ? true : prev.failed,
      };
    });
  }, []);

  const connect = useCallback(
    async (jId: string) => {
      // Close any existing connection
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }

      // Get auth token for the connection
      let token: string | null = null;
      try {
        token = (await auth.currentUser?.getIdToken()) ?? null;
      } catch {
        // best-effort
      }

      const wsUrl = token
        ? `${API_CONFIG.websocket.url}?token=${encodeURIComponent(token)}`
        : API_CONFIG.websocket.url;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        setState((prev) => ({ ...prev, connected: true }));
        ws.send(JSON.stringify({ type: 'subscribe_progress', jobId: jId }));
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          const msg: WsMessage = JSON.parse(event.data as string);

          switch (msg.type) {
            case 'subscription_confirmed':
              setState((prev) => ({ ...prev, active: true }));
              break;

            case 'scraping_progress':
              if (msg.data) {
                applyData(msg.data, null);
              }
              break;

            case 'scraping_complete':
              if (msg.data) {
                applyData(msg.data, 'completed');
              }
              break;

            case 'scraping_error':
              if (msg.data) {
                applyData(msg.data, 'failed');
              }
              break;
          }
        } catch {
          // Ignore unparseable messages
        }
      };

      ws.onerror = () => {
        if (!mountedRef.current) return;
        setState((prev) => ({ ...prev, connected: false }));
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        wsRef.current = null;
        setState((prev) => {
          // Don't reconnect if job is already terminal
          if (!prev.completed && !prev.failed && !hasReconnectedRef.current) {
            hasReconnectedRef.current = true;
            reconnectTimerRef.current = setTimeout(() => {
              if (mountedRef.current) {
                // Allow future reconnects after this one
                hasReconnectedRef.current = false;
                connect(jId);
              }
            }, 2000);
          }
          return { ...prev, connected: false };
        });
      };
    },
    [applyData],
  );

  useEffect(() => {
    mountedRef.current = true;
    hasReconnectedRef.current = false;

    if (!jobId) {
      // Reset when no job
      setState(INITIAL_STATE);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
      return;
    }

    connect(jobId);

    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [jobId, connect]);

  return state;
}
